import { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useDemoRole } from '../../contexts/DemoRoleContext';
import { useUserOrganizations } from '../../assistants/drew/hooks/useOrganization';

/**
 * Who is looking at a dashboard, in which organization, with which stored role — and, under
 * View As (P4-03 / P3-11), whose layout to show (read-only). Platform-level (§1c): the same
 * answer serves the REPSit dashboard and every IA dashboard.
 *
 * Returns { uid, orgId, organization, role, viewAsUid, layoutUid, readOnly, loading }.
 *   role       stored role key (org_admin … respondent) of the person whose dashboard shows;
 *              null while unresolved (which hides nothing — registry convention)
 *   layoutUid  whose saved layout to load (the member's under View As, else the viewer's)
 */
export function useDashboardViewer() {
  const { user } = useAuth();
  const { roles, loading: userLoading } = useUser();
  const { viewingAs, viewAsUid } = useDemoRole();
  const { organizations, loading: orgLoading } = useUserOrganizations(user?.uid);
  const organization = organizations[0] || null;
  const orgId = organization?.id || null;

  return useMemo(() => {
    const ownRole = (orgId && roles && roles[orgId]?.role) || null;
    const isOrgAdmin = Boolean(user?.uid && Array.isArray(organization?.admins) && organization.admins.includes(user.uid));
    const role = viewingAs ? viewingAs.role || null : ownRole || (isOrgAdmin ? 'org_admin' : null);
    return {
      uid: user?.uid || null,
      orgId,
      organization,
      role,
      viewAsUid: viewingAs ? viewAsUid : null,
      layoutUid: viewingAs && viewAsUid ? viewAsUid : user?.uid || null,
      readOnly: Boolean(viewingAs),
      loading: Boolean(userLoading || orgLoading),
    };
  }, [user?.uid, roles, orgId, organization, viewingAs, viewAsUid, userLoading, orgLoading]);
}

export default useDashboardViewer;
